/**
 * Cart Page Component
 *
 * Full cart page with item list, quantities, and checkout link.
 */

import { useCart, useShop } from '@/context';
import { Button } from '../shared/Button';
import { QuantityInput } from '../shared/QuantityInput';
import type { CartItem } from '@/types';
import styles from './CartPage.module.css';

/**
 * Build checkout URL with session key for guest users.
 * This helps maintain cart state when cookies are blocked (private browsing).
 */
function getCheckoutUrl(baseUrl: string): string {
  const config = window.evcShop;
  if (!config.isLoggedIn && config.sessionKey) {
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}evcs_session=${encodeURIComponent(config.sessionKey)}`;
  }
  return baseUrl;
}

export function CartPage() {
  const { cart, isLoading, isUpdating, updateItem, removeItem, isEmpty } = useCart();
  const { formatPrice, t, urls } = useShop();

  if (isLoading && isEmpty) {
    return (
      <div className={styles.container}>
        <div className={styles.skeletonTitle} />
        <div className={styles.content}>
          <div className={styles.items}>
            {[1, 2].map((i) => (
              <div key={i} className={styles.skeletonItem}>
                <div className={styles.skeletonImage} />
                <div className={styles.skeletonDetails}>
                  <div className={styles.skeletonName} />
                  <div className={styles.skeletonPrice} />
                </div>
                <div className={styles.skeletonQty} />
                <div className={styles.skeletonSubtotal} />
              </div>
            ))}
          </div>
          <aside className={styles.summary}>
            <div className={styles.skeletonSummaryTitle} />
            <div className={styles.skeletonSummaryRows}>
              <div className={styles.skeletonRow} />
              <div className={styles.skeletonRow} />
            </div>
            <div className={styles.skeletonButton} />
          </aside>
        </div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className={styles.empty}>
        <div className={styles.emptyIcon}>
          <CartIcon />
        </div>
        <h2>{t('emptyCart')}</h2>
        <p>Votre panier est vide. Découvrez nos produits !</p>
        <Button
          variant="primary"
          size="large"
          onClick={() => (window.location.href = urls.shop)}
        >
          Découvrir nos produits
        </Button>
      </div>
    );
  }

  const handleQuantityChange = async (item: CartItem, newQuantity: number) => {
    if (newQuantity < 1) {
      await removeItem(item.key);
    } else {
      await updateItem(item.key, newQuantity);
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{t('cart')}</h1>

      <div className={styles.content}>
        <div className={styles.items}>
          {cart.items.map((item) => (
            <div
              key={item.key}
              className={`${styles.item} ${isUpdating === item.key ? styles.updating : ''}`}
            >
              <div className={styles.itemImage}>
                <img src={item.image} alt={item.name} />
              </div>

              <div className={styles.itemDetails}>
                <h3 className={styles.itemName}>{item.name}</h3>
                {item.variation_attributes && Object.keys(item.variation_attributes).length > 0 && (
                  <div className={styles.itemVariation}>
                    {Object.entries(item.variation_attributes).map(([key, value]) => (
                      <span key={key}>{value}</span>
                    ))}
                  </div>
                )}
                {item.meta ? (
                  <div className={styles.itemMeta} style={{ fontSize: '0.9em', color: '#666', marginTop: '8px' }}>
                    {item.meta.options && Array.isArray(item.meta.options) ? (
                      item.meta.options.map((opt: any, idx: number) => (
                        <div key={idx} style={{ marginBottom: idx < item.meta!.options.length - 1 ? '8px' : 0 }}>
                          <strong>{opt.option_name || 'Personnalisation'}</strong>
                          <ul style={{ paddingLeft: '0', listStyle: 'none', margin: '4px 0' }}>
                            {opt.initials && <li>Initiales: {opt.initials}</li>}
                            {opt.names && <li>Prénoms: {opt.names}</li>}
                            {opt.date && <li>Date: {opt.date}</li>}
                            {opt.message && <li>Message: {opt.message}</li>}
                            {opt.color && <li>Couleur: {opt.color}</li>}
                            {opt.font && <li>Police: {opt.font}</li>}
                            {opt.location && <li>Emplacement: {opt.location}</li>}
                          </ul>
                        </div>
                      ))
                    ) : (
                      <>
                        <strong>{item.meta.option_name || 'Personnalisation'}</strong>
                        <ul style={{ paddingLeft: '0', listStyle: 'none', margin: '4px 0' }}>
                          {item.meta.initials && <li>Initiales: {item.meta.initials}</li>}
                          {item.meta.names && <li>Prénoms: {item.meta.names}</li>}
                          {item.meta.date && <li>Date: {item.meta.date}</li>}
                          {item.meta.message && <li>Message: {item.meta.message}</li>}
                          {item.meta.color && <li>Couleur: {item.meta.color}</li>}
                          {item.meta.font && <li>Police: {item.meta.font}</li>}
                        </ul>
                      </>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: '0.9em', color: '#999', marginTop: '8px' }}>
                    Sans personnalisation
                  </div>
                )}
                <div className={styles.itemPrice}>{formatPrice(item.price)}</div>
              </div>

              <div className={styles.itemQuantity}>
                <QuantityInput
                  value={item.quantity}
                  min={1}
                  max={item.stock}
                  onChange={(qty) => handleQuantityChange(item, qty)}
                  disabled={isUpdating === item.key}
                />
              </div>

              <div className={styles.itemSubtotal}>
                {formatPrice(item.price * item.quantity)}
              </div>

              <button
                className={styles.removeButton}
                onClick={() => removeItem(item.key)}
                disabled={isUpdating === item.key}
                aria-label={t('removeFromCart')}
              >
                <TrashIcon />
              </button>
            </div>
          ))}

          <a href={urls.shop} className={styles.continueShopping}>
            ← Continuer mes achats
          </a>
        </div>

        <aside className={styles.summary}>
          <h2 className={styles.summaryTitle}>Résumé</h2>

          <div className={styles.summaryRows}>
            <div className={styles.summaryRow}>
              <span>{t('subtotal')}</span>
              <span>{formatPrice(cart.totals.subtotal)}</span>
            </div>

            {cart.totals.tax > 0 && (
              <div className={styles.summaryRow}>
                <span>TVA</span>
                <span>{formatPrice(cart.totals.tax)}</span>
              </div>
            )}

            <div className={`${styles.summaryRow} ${styles.total}`}>
              <span>{t('total')}</span>
              <span>{formatPrice(cart.totals.total)}</span>
            </div>
          </div>

          <Button
            variant="primary"
            size="large"
            fullWidth
            onClick={() => (window.location.href = getCheckoutUrl(urls.checkout))}
          >
            {t('checkout')}
          </Button>
        </aside>
      </div>
    </div>
  );
}

function CartIcon() {
  return (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}
